/**
 * Data-driven SVG charts for the depositor analytics — plotted against REAL unix
 * timestamps (unlike the operator charts.tsx which assume a rolling "last N
 * days" buffer). Pure SVG + React, matching the console house style (dashed
 * gridlines, hover crosshair + tooltip).
 */

import { useMemo, useRef, useState, type CSSProperties } from "react";

function fmtDay(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ─────────────────────────── Multi-line time series ─────────────────────────
 * `points` defines the shared x-axis (its `timestamp`); each `line` is a y
 * accessor over the same points. Used for PnL / value / cost-basis, which the
 * API returns aligned per bucket.
 */
export interface SeriesLine<T> {
  label: string;
  color: string;
  get: (p: T) => number;
  dashed?: boolean;
  area?: boolean;
}

export function TimeSeriesChart<T extends { timestamp: number }>({
  points,
  lines,
  height = 240,
  valueFmt = (v: number) => String(v),
}: {
  points: T[];
  lines: SeriesLine<T>[];
  height?: number;
  valueFmt?: (v: number) => string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const gid = useMemo(() => "ts" + Math.random().toString(36).slice(2, 7), []);

  const W = 1000;
  const H = height;
  const pad = { t: 16, b: 26, l: 0, r: 0 };

  const { min, span } = useMemo(() => {
    const vals: number[] = [];
    for (const p of points) for (const l of lines) vals.push(l.get(p));
    if (vals.length === 0) return { min: 0, span: 1 };
    let lo = Math.min(...vals);
    let hi = Math.max(...vals);
    if (lo > 0) lo = 0; // anchor to zero when all-positive so gains read from 0
    if (hi < 0) hi = 0;
    const s = hi - lo || 1;
    return { min: lo, span: s };
  }, [points, lines]);

  if (points.length < 2) {
    return (
      <div style={{ height, display: "grid", placeItems: "center", color: "var(--micro)", fontSize: 12.5 }}>
        Not enough history yet.
      </div>
    );
  }

  const X = (i: number) => pad.l + (i / (points.length - 1)) * (W - pad.l - pad.r);
  const Y = (v: number) => pad.t + (1 - (v - min) / span) * (H - pad.t - pad.b);
  const path = (l: SeriesLine<T>) =>
    points.map((p, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(l.get(p)).toFixed(1)}`).join(" ");

  const onMove = (e: React.MouseEvent) => {
    const r = wrapRef.current!.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    setHover(Math.max(0, Math.min(points.length - 1, Math.round(px * (points.length - 1)))));
  };
  const zeroY = Y(0);

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
        <defs>
          {lines.map((l, li) => (
            <linearGradient key={li} id={`${gid}-${li}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={l.color} stopOpacity="0.22" />
              <stop offset="1" stopColor={l.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>
        {[0.25, 0.5, 0.75].map((g, i) => (
          <line key={i} x1="0" x2={W} y1={pad.t + g * (H - pad.t - pad.b)} y2={pad.t + g * (H - pad.t - pad.b)} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 6" vectorEffect="non-scaling-stroke" />
        ))}
        {/* zero baseline when the domain crosses it */}
        {min < 0 && (
          <line x1="0" x2={W} y1={zeroY} y2={zeroY} stroke="var(--micro)" strokeWidth="1" opacity="0.4" vectorEffect="non-scaling-stroke" />
        )}
        {lines.map((l, li) =>
          l.area ? (
            <path
              key={`a${li}`}
              d={`${path(l)} L${X(points.length - 1)} ${H - pad.b} L${X(0)} ${H - pad.b} Z`}
              fill={`url(#${gid}-${li})`}
            />
          ) : null,
        )}
        {lines.map((l, li) => (
          <path
            key={`l${li}`}
            d={path(l)}
            fill="none"
            stroke={l.color}
            strokeWidth={l.dashed ? 1.8 : 2.4}
            strokeDasharray={l.dashed ? "5 4" : undefined}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {hover != null && (
          <g>
            <line x1={X(hover)} x2={X(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--text)" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" vectorEffect="non-scaling-stroke" />
            {lines.map((l, li) => (
              <circle key={li} cx={X(hover)} cy={Y(l.get(points[hover]))} r="4.5" fill="var(--surface)" stroke={l.color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        )}
      </svg>
      {hover != null && (
        <div
          style={{
            position: "absolute",
            left: `${(hover / (points.length - 1)) * 100}%`,
            top: -8,
            transform: `translateX(${hover < points.length * 0.2 ? "0" : hover > points.length * 0.8 ? "-100%" : "-50%"})`,
            pointerEvents: "none",
            background: "var(--text)",
            color: "#fff",
            padding: "9px 12px",
            borderRadius: 10,
            boxShadow: "var(--pop-shadow)",
            whiteSpace: "nowrap",
            zIndex: 5,
            minWidth: 140,
          }}
        >
          <div className="mono" style={{ fontSize: 9.5, opacity: 0.6, letterSpacing: "0.06em", marginBottom: 4 }}>
            {fmtDay(points[hover].timestamp)}
          </div>
          {lines.map((l, li) => (
            <div key={li} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: l.color }} />
                <span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>{l.label}</span>
              </span>
              <span className="mono tnum" style={{ fontSize: 12.5, fontWeight: 600 }}>{valueFmt(l.get(points[hover]))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── Diverging bar timemap ──────────────────────────
 * Daily bars: `up` grows upward from the baseline (deposits), optional `down`
 * grows downward (redemptions). One bar per row.
 */
export function BarTimemap<T extends { day: number }>({
  bars,
  up,
  down,
  height = 200,
  valueFmt = (v: number) => String(v),
}: {
  bars: T[];
  up: { color: string; get: (b: T) => number; label: string };
  down?: { color: string; get: (b: T) => number; label: string };
  height?: number;
  valueFmt?: (v: number) => string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = height;
  const pad = { t: 12, b: 22 };

  const maxUp = useMemo(() => Math.max(1, ...bars.map((b) => up.get(b))), [bars, up]);
  const maxDown = useMemo(() => (down ? Math.max(0, ...bars.map((b) => down.get(b))) : 0), [bars, down]);
  const total = maxUp + maxDown || 1;
  const baseline = pad.t + (maxUp / total) * (H - pad.t - pad.b);

  if (bars.length === 0) {
    return (
      <div style={{ height, display: "grid", placeItems: "center", color: "var(--micro)", fontSize: 12.5 }}>
        No activity in this window.
      </div>
    );
  }

  const n = bars.length;
  const slot = W / n;
  const bw = Math.max(2, Math.min(slot * 0.6, 26));
  const upH = (v: number) => (v / total) * (H - pad.t - pad.b);

  const onMove = (e: React.MouseEvent) => {
    const r = wrapRef.current!.getBoundingClientRect();
    setHover(Math.max(0, Math.min(n - 1, Math.floor(((e.clientX - r.left) / r.width) * n))));
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
        <line x1="0" x2={W} y1={baseline} y2={baseline} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {bars.map((b, i) => {
          const cx = slot * i + slot / 2;
          const u = up.get(b);
          const d = down ? down.get(b) : 0;
          return (
            <g key={i} opacity={hover == null || hover === i ? 1 : 0.5}>
              {u > 0 && (
                <rect x={cx - bw / 2} y={baseline - upH(u)} width={bw} height={upH(u)} rx="2" fill={up.color} />
              )}
              {down && d > 0 && (
                <rect x={cx - bw / 2} y={baseline} width={bw} height={upH(d)} rx="2" fill={down.color} />
              )}
            </g>
          );
        })}
        {hover != null && (
          <line x1={slot * hover + slot / 2} x2={slot * hover + slot / 2} y1={pad.t} y2={H - pad.b} stroke="var(--text)" strokeWidth="1" strokeDasharray="3 3" opacity="0.3" vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      {hover != null && (
        <div
          style={{
            position: "absolute",
            left: `${((hover + 0.5) / n) * 100}%`,
            top: -8,
            transform: `translateX(${hover < n * 0.2 ? "0" : hover > n * 0.8 ? "-100%" : "-50%"})`,
            pointerEvents: "none",
            background: "var(--text)",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: 10,
            boxShadow: "var(--pop-shadow)",
            whiteSpace: "nowrap",
            zIndex: 5,
          } as CSSProperties}
        >
          <div className="mono" style={{ fontSize: 9.5, opacity: 0.6, letterSpacing: "0.06em", marginBottom: 4 }}>
            {fmtDay(bars[hover].day)}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: 2, background: up.color }} />
              <span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>{up.label}</span>
            </span>
            <span className="mono tnum" style={{ fontSize: 12.5, fontWeight: 600 }}>{valueFmt(up.get(bars[hover]))}</span>
          </div>
          {down && (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 3 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: down.color }} />
                <span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>{down.label}</span>
              </span>
              <span className="mono tnum" style={{ fontSize: 12.5, fontWeight: 600 }}>{valueFmt(down.get(bars[hover]))}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
